import React from 'react';
import AdminDashboard from './AdminDashboard.js';
import AdminMessages from './AdminMessages.js';
import AdminUsers from './AdminUsers.js';
import AdminDonations from './AdminDonations.js';
import AdminPartnerships from './AdminPartnerships.js';
import AdminEarlyAccess from './AdminEarlyAccess.js';
import AdminCarbonTokens from './AdminCarbonTokens.js';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

function AdminPanel() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = React.useState('dashboard');
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [supabaseClient, setSupabaseClient] = React.useState(null);
    
    // Initialize Supabase client
    React.useEffect(() => {
        const initSupabase = async () => {
            try {
                // Try using the global helper function first
                if (typeof window.ensureSupabaseClient === 'function') {
                    const client = await window.ensureSupabaseClient();
                    if (client) {
                        console.log('Supabase client initialized for admin panel via global helper');
                        setSupabaseClient(client);
                        return;
                    }
                }
                
                // Try to get Supabase client from window
                let client = window.supabaseClient;
                
                // If not available from window, try to get it using the utility function
                if (!client) {
                    client = getSupabaseClient();
                }
                
                // If still not available, try to use the service initializer
                if (!client && window.initializeServices) {
                    await window.initializeServices();
                    client = window.supabaseClient;
                }
                
                // If we have a client now, set it
                if (client) {
                    console.log('Supabase client initialized for admin panel');
                    setSupabaseClient(client);
                } else {
                    throw new Error('Could not initialize Supabase client');
                }
            } catch (err) {
                console.error('Supabase initialization error:', err);
                setError('Failed to initialize Supabase: ' + err.message);
                setIsLoading(false);
            }
        };
        
        initSupabase();
    }, []);
    
    // Verify admin status once we have the Supabase client and user
    React.useEffect(() => {
        // Check if the user is actually an admin
        const verifyAdmin = async () => {
            try {
                if (!supabaseClient) {
                    // Skip verification if client isn't ready yet
                    return;
                }
                
                if (!user || !user.id) {
                    throw new Error('User information is missing');
                }
                
                // Try profiles table first (newer schema)
                let { data, error } = await supabaseClient
                    .from('profiles')
                    .select('role')
                    .eq('id', user.id)
                    .single();
                    
                // If not found in profiles, try users table (older schema)
                if (error || !data) {
                    ({ data, error } = await supabaseClient
                        .from('users')
                        .select('role')
                        .eq('id', user.id)
                        .single());
                }
                
                if (error) throw error;
                
                if (!data || data.role !== 'admin') {
                    setIsAdmin(false);
                    setError('You do not have admin privileges.');
                } else {
                    setIsAdmin(true);
                }
                
            } catch (err) {
                console.error('Admin verification error:', err);
                setError(err.message || 'Failed to verify admin status');
                setIsAdmin(false);
            } finally {
                setIsLoading(false);
            }
        };
        
        verifyAdmin();
    }, [user, supabaseClient]);
    
    // Simple tabs system
    const renderTabContent = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                </div>
            );
        }
        
        if (!isAdmin || error) {
            return (
                <div className="bg-red-50 p-6 rounded-lg">
                    <h2 className="text-xl font-bold text-red-700">Access Denied</h2>
                    <p className="text-red-600">{error || 'You do not have permission to access this area.'}</p>
                </div>
            );
        }
        
        switch(activeTab) {
            case 'dashboard':
                return <AdminDashboard user={user} supabaseClient={supabaseClient} />;
            case 'messages':
                return <AdminMessages supabaseClient={supabaseClient} />;
            case 'users':
                return <AdminUsers currentUser={user} supabaseClient={supabaseClient} />;
            case 'donations':
                return <AdminDonations supabaseClient={supabaseClient} />;
            case 'partnerships':
                return <AdminPartnerships supabaseClient={supabaseClient} />;
            case 'carbon-tokens':
                return <AdminCarbonTokens supabaseClient={supabaseClient} />;
            case 'early-access':
                return <AdminEarlyAccess supabaseClient={supabaseClient} />;
            default:
                return <AdminDashboard user={user} supabaseClient={supabaseClient} />;
        }
    };
    
    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Header */}
                <div className="bg-gray-100 p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-bold text-gray-800">
                            Admin Dashboard
                        </h1>
                        <div className="flex items-center space-x-4">
                            <span className="text-gray-600">
                                <i className="fas fa-user-circle mr-2"></i>
                                {user?.name || 'User'}
                            </span>
                        </div>
                    </div>
                </div>
                
                {/* Navigation - Only show if admin */}
                {isAdmin && !isLoading && (
                    <div className="flex border-b border-gray-200 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'dashboard' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-tachometer-alt mr-2"></i>
                            Dashboard
                        </button>
                        <button
                            onClick={() => setActiveTab('messages')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'messages' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-envelope mr-2"></i>
                            Messages
                        </button>
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'users' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-users mr-2"></i>
                            Users
                        </button>
                        <button
                            onClick={() => setActiveTab('donations')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'donations' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-gift mr-2"></i>
                            Donations
                        </button>
                        <button
                            onClick={() => setActiveTab('partnerships')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'partnerships' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-handshake mr-2"></i>
                            Partnerships
                        </button>
                        <button
                            onClick={() => setActiveTab('carbon-tokens')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'carbon-tokens' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-coins mr-2"></i>
                            Carbon Tokens
                        </button>
                        <button
                            onClick={() => setActiveTab('early-access')}
                            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'early-access' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
                        >
                            <i className="fas fa-rocket mr-2"></i>
                            Early Access
                        </button>
                    </div>
                )}
                
                {/* Content Area */}
                <div className="p-6">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
}

window.AdminPanel = AdminPanel;

export default AdminPanel;
